if (!customElements.get('back-in-stock-modal')) {
  customElements.define(
    'back-in-stock-modal',
    class BackInStockModal extends HTMLElement {
      constructor() {
        super();
        this._onKeydown = this._onKeydown.bind(this);
        this._onOverlayClick = this._onOverlayClick.bind(this);
        this._onTouchStart = this._onTouchStart.bind(this);
        this._onTouchMove = this._onTouchMove.bind(this);
        this._onTouchEnd = this._onTouchEnd.bind(this);
        this._onSubmit = this._onSubmit.bind(this);
        this._validateForm = this._validateForm.bind(this);
        this._triggerEl = null;
        this._dragY = 0;
        this._dragStart = 0;
      }

      connectedCallback() {
        this.overlay = this.querySelector('[data-bis-overlay]');
        this.panel = this.querySelector('.bis-modal__panel');
        this.handle = this.querySelector('[data-bis-handle]');
        this.closeBtn = this.querySelector('[data-bis-close]');
        this.form = this.querySelector('[data-bis-form]');
        this.submitBtn = this.querySelector('[data-bis-submit]');
        this.feedback = this.querySelector('[data-bis-feedback]');
        this.nameInput = this.querySelector('[data-bis-name]');
        this.emailInput = this.querySelector('[data-bis-email]');
        this.consentInput = this.querySelector('[data-bis-consent]');

        this.overlay && this.overlay.addEventListener('click', this._onOverlayClick);
        this.closeBtn && this.closeBtn.addEventListener('click', () => this.close());
        this.handle && this.handle.addEventListener('touchstart', this._onTouchStart, { passive: true });
        this.form && this.form.addEventListener('submit', this._onSubmit);

        [this.nameInput, this.emailInput, this.consentInput].forEach((el) => {
          el && el.addEventListener('input', this._validateForm);
          el && el.addEventListener('change', this._validateForm);
        });
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this._onKeydown);
        this.handle && this.handle.removeEventListener('touchmove', this._onTouchMove);
        this.handle && this.handle.removeEventListener('touchend', this._onTouchEnd);
      }

      open(triggerEl) {
        this._triggerEl = triggerEl || document.activeElement;

        if (triggerEl && triggerEl.dataset) {
          const d = triggerEl.dataset;
          this.dataset.productId = d.productId || '';
          this.dataset.variantId = d.variantId || '';
          this.dataset.productTitle = d.productTitle || '';
          this.dataset.productUrl = d.productUrl || '';
          this.dataset.productImage = d.productImage || '';
          this.dataset.productVendor = d.productVendor || '';

          const titleEls = this.querySelectorAll('[data-bis-product-title], [data-bis-product-title-card]');
          titleEls.forEach((el) => {
            el.textContent = d.productTitle || 'este libro';
          });

          const card = this.querySelector('[data-bis-product-card]');
          const imgEl = this.querySelector('[data-bis-product-image]');
          const vendorEl = this.querySelector('[data-bis-product-vendor]');

          if (d.productImage && imgEl && card) {
            imgEl.src = d.productImage;
            imgEl.alt = d.productTitle || '';
            vendorEl.textContent = d.productVendor || '';
            vendorEl.hidden = !d.productVendor;
            card.hidden = false;
          } else if (card) {
            card.hidden = true;
          }
        }

        this.hidden = false;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', this._onKeydown);

        if (this.feedback) {
          this.feedback.textContent = '';
          this.feedback.removeAttribute('data-type');
        }

        if (this.form) this.form.reset();
        this._validateForm();

        requestAnimationFrame(() => {
          this.nameInput && this.nameInput.focus();
        });
      }

      close() {
        if (this.hidden) return;
        this.classList.add('is-closing');
        const duration = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 1 : 300;
        setTimeout(() => {
          this.hidden = true;
          this.classList.remove('is-closing');
          this.panel.style.transform = '';
          document.body.style.overflow = '';
          document.removeEventListener('keydown', this._onKeydown);
          if (this._triggerEl && this._triggerEl.focus) this._triggerEl.focus();
        }, duration);
      }

      _onKeydown(e) {
        if (e.key === 'Escape') {
          e.preventDefault();
          this.close();
          return;
        }
        if (e.key === 'Tab') this._trapFocus(e);
      }

      _trapFocus(e) {
        const focusables = this.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }

      _onOverlayClick(e) {
        if (e.target === this.overlay) this.close();
      }

      _onTouchStart(e) {
        this._dragStart = e.touches[0].clientY;
        this.classList.add('is-dragging');
        this.handle.addEventListener('touchmove', this._onTouchMove, { passive: false });
        this.handle.addEventListener('touchend', this._onTouchEnd);
      }

      _onTouchMove(e) {
        const dy = e.touches[0].clientY - this._dragStart;
        if (dy > 0) {
          this._dragY = dy;
          this.panel.style.transform = 'translateY(' + dy + 'px)';
          e.preventDefault();
        }
      }

      _onTouchEnd() {
        this.classList.remove('is-dragging');
        this.handle.removeEventListener('touchmove', this._onTouchMove);
        this.handle.removeEventListener('touchend', this._onTouchEnd);
        if (this._dragY > 120) {
          this.close();
        } else {
          this.panel.style.transform = '';
        }
        this._dragY = 0;
      }

      _isValidEmail(value) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);
      }

      _validateForm() {
        if (!this.submitBtn) return;
        const nameOk = this.nameInput && this.nameInput.value.trim().length >= 2;
        const emailOk = this.emailInput && this._isValidEmail(this.emailInput.value.trim());
        const consentOk = this.consentInput && this.consentInput.checked;
        this.submitBtn.disabled = !(nameOk && emailOk && consentOk);

        if (this.emailInput) {
          this.emailInput.setAttribute(
            'aria-invalid',
            this.emailInput.value && !emailOk ? 'true' : 'false'
          );
        }
      }

      async _onSubmit(e) {
        e.preventDefault();
        if (this.submitBtn.disabled) return;

        const payload = {
          name: this.nameInput.value.trim(),
          email: this.emailInput.value.trim(),
          product_id: this.dataset.productId || '',
          variant_id: this.dataset.variantId || null,
          product_title: this.dataset.productTitle || '',
          product_url: this.dataset.productUrl || '',
          market: (window.Shopify && window.Shopify.country) || 'CO',
          locale: (window.Shopify && window.Shopify.locale) || 'es',
          shop: (window.Shopify && window.Shopify.shop) || '',
        };

        this.submitBtn.setAttribute('aria-busy', 'true');
        this.feedback.textContent = '';
        this.feedback.removeAttribute('data-type');

        const endpoint = this.dataset.endpoint || 'https://back-in-stock-poiema.poiemashopify.workers.dev/subscribe';

        try {
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify(payload),
          });

          const data = await response.json().catch(() => ({}));

          if (response.ok) {
            this.feedback.setAttribute('data-type', 'success');
            this.feedback.textContent =
              data.message ||
              '¡Listo! Te avisaremos a tu correo cuando esté disponible. Tu cupón del 15% te llegará junto al aviso.';
            this.form.reset();
            this._validateForm();
            this.dispatchEvent(
              new CustomEvent('bis:subscribed', { bubbles: true, detail: payload })
            );
          } else {
            this.feedback.setAttribute('data-type', 'error');
            this.feedback.textContent =
              (data && data.error) ||
              'No pudimos guardar tu suscripción. Por favor inténtalo de nuevo en unos minutos.';
          }
        } catch (err) {
          this.feedback.setAttribute('data-type', 'error');
          this.feedback.textContent =
            'No pudimos conectar con el servidor. Verifica tu conexión e inténtalo de nuevo.';
        } finally {
          this.submitBtn.removeAttribute('aria-busy');
        }
      }
    }
  );
}

document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-bis-trigger]');
  if (!btn) return;
  e.preventDefault();
  const modal = document.getElementById('BackInStockModalGlobal');
  if (modal && typeof modal.open === 'function') modal.open(btn);
});

